import { Alert, Box, Button, CircularProgress, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import Divider from 'components/divider';
import List, { HeadCell } from 'components/list/list';
import ListCell from 'components/list/list-cell';
import ListRow, { BodyTableRow } from 'components/list/list-row';
import LoadingSpinner from 'components/loading-spinner';
import Paper from 'components/paper';
import RadioSelectItem from 'components/radio-select-item';
import Select from 'components/select';
import TextField from 'components/text-field';
import Tooltip from 'components/tooltip';
import { useAccountState } from 'contexts/account/account-context';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import { useGet } from 'hooks/use-get';
import { usePost } from 'hooks/use-post';
import { AlertTriangle } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import { FormattedDate, FormattedMessage, FormattedNumber, FormattedTime, useIntl } from 'react-intl';
import _ from 'underscore';
import { formatPhoneNumber } from 'utils/format-phone-numbers';
import { getSiteSettings } from 'views/site-settings/api/site-settings';
import { getOrderDistribution } from 'views/order-feed/features/order-distribution/api/get-distributions';
import { getSiloManagement } from 'views/order-feed/features/order-distribution/api/get-distribution-silos';
import { DistributedQuantities } from 'views/order-feed/features/order-distribution/types/distributed-quantities.type';
import { OrderDistribution } from 'views/order-feed/features/order-distribution/types/order-distribution.type';
import { UpdateOrderDistributionInput } from 'views/order-feed/features/order-distribution/types/update-order-distribution-input.type';
import { SiloDistribution } from 'views/order-feed/features/order-distribution/types/silo-distribution.type';
import { putOrderDistribution } from 'views/order-feed/features/order-distribution/api/update-distribution';
import DistributeSiloForm from 'views/order-feed/features/order-distribution/components/distribute-silo-form';

//#region CONSTS

type SiloDistributionForm = {
  name: string;
  capacity: number;
  siloId: string;
  isExtraStorage: boolean;
  siloDistributions: SiloDistribution[];
  hasConfigChanged: boolean;
  description: string | null;
  isEnabled: boolean;
};

export type CreateOrderDistributionForm = {
  comments: string;
  pointOfContact: string;
  silos: SiloDistributionForm[];
};

//#endregion CONSTS

type Props = {
  distributionId: string;
};

const PAGE_NAME_ID = 'orderFeed.orderDistribution.createDistribution';

const CreateOrderDistribution: React.FC<Props> = ({ distributionId }) => {
  const { account } = useAccountState();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const snackbarDispatch = useSnackbarDispatch();
  const intl = useIntl();
  const queryClient = useQueryClient();

  const { data: distribution, isLoading: isDistributionLoading } = useGet(
    ['getDistribution', account?.company?.customerErpId, distributionId],
    ({ queryKey }) => getOrderDistribution(String(queryKey[1]), String(queryKey[2])),
    {
      enabled: !!distributionId,
    }
  );

  const { data: siteSettings, isLoading: isSitesSettingsLoading } = useGet(
    ['getSiteSettings', distribution?.siteId],
    ({ queryKey }) => getSiteSettings(String(queryKey[1])),
    {
      enabled: !!distribution?.siteId,
    }
  );

  //#region STATES

  const [distributed, setDistributed] = useState<DistributedQuantities>(
    Object.fromEntries(distribution?.distributions?.map(({ itemNumber, quantity }) => [itemNumber, quantity]) || [])
  );

  const options: HeadCell[] = [
    {
      id: 'siteName',
      label: intl.formatMessage({ id: `${PAGE_NAME_ID}.overview.list.siteName` }),
    },
    {
      id: 'dispatchDate',
      label: intl.formatMessage({ id: `${PAGE_NAME_ID}.overview.list.dispatchDate` }),
    },
    {
      id: 'eta',
      label: intl.formatMessage({ id: `${PAGE_NAME_ID}.overview.list.deliveryDate` }),
    },
    {
      id: 'orderQuantity',
      label: intl.formatMessage({ id: `${PAGE_NAME_ID}.overview.list.orderQuantity` }),
      align: 'right',
    },
  ];

  const bodyOptions: HeadCell[] = [
    { id: 'warnings', label: '', autoWidth: true },
    { id: 'product', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.overview.list.body.product` }) },
    {
      id: 'ordered',
      label: intl.formatMessage({ id: `${PAGE_NAME_ID}.overview.list.body.ordered` }),
      align: 'right',
    },
    {
      id: 'toDistribute',
      label: intl.formatMessage({ id: `${PAGE_NAME_ID}.overview.list.body.toDistribute` }),
      align: 'right',
      sx: {
        whiteSpace: 'nowrap',
      },
    },
    {
      id: 'distributed',
      label: intl.formatMessage({ id: `${PAGE_NAME_ID}.overview.list.body.distributed` }),
      align: 'right',
    },
  ];
  //#endregion STATES

  //#region FETCH

  const originalToDistribute: DistributedQuantities = useMemo(
    () => Object.fromEntries(distribution?.products?.map(({ itemNumber, quantity }) => [itemNumber, quantity]) || []),
    [distribution]
  );

  const { data: silos, isLoading: isSilosLoading } = useGet(
    ['getSiloManagement', distribution?.siteId],
    ({ queryKey }) => getSiloManagement(String(queryKey[1])),
    {
      select: (data) => {
        const silosChanged = data?.map((sl) => {
          return {
            ...sl,
            hasConfigChanged:
              distribution?.distributions.find((d) => {
                if (!sl.name && sl.name.includes('0') && !d.siloName) {
                  return d.siloName.split('0')[1].toLocaleLowerCase() === sl.name.toLocaleLowerCase();
                }
                return d.siloName.toLocaleLowerCase() === sl.name.toLocaleLowerCase();
              })?.siloId === null,
          };
        });

        const t = silosChanged.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));

        return _.chain(t).sortBy('isExtraStorage').value();
      },
      enabled: !!distribution?.siteId,
    }
  );

  //#endregion FETCH

  //#region POST

  const postDistribution = usePost<OrderDistribution, UpdateOrderDistributionInput>(
    (request) => putOrderDistribution(account.company!.customerErpId, distribution!.id, request),
    () => {
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });
      queryClient.invalidateQueries({
        queryKey: ['getOrderDistributions'],
      });
      queryClient.invalidateQueries({
        queryKey: ['getDistribution', account.company!.customerErpId, distribution!.id],
      });
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );

  //#endregion POST

  //#region FORMS

  const defaultValues: CreateOrderDistributionForm = useMemo(() => {
    const siloFormValues = silos?.map((silo) => {
      const filteredDistributions = distribution?.distributions.filter((dist) => dist.siloId === silo.id);

      const distributionFormValues: SiloDistribution[] =
        filteredDistributions?.map((dist) => ({
          itemNumber: dist.itemNumber,
          siloId: String(dist.siloId),
          quantity: dist.quantity,
        })) || [];

      return {
        name: silo.name,
        capacity: silo.isExtraStorage ? Infinity : silo.capacity,
        siloId: silo.id,
        isExtraStorage: silo.isExtraStorage,
        hasConfigChanged: Boolean(silo.hasConfigChanged),
        description: silo.description,
        isEnabled: silo.isEnabled,

        siloDistributions:
          distributionFormValues.length > 0
            ? distributionFormValues
            : [
                {
                  itemNumber: '',
                  siloId: silo.id,
                  quantity: 0,
                },
              ],
      };
    });

    return {
      pointOfContact: distribution?.pointOfContact || '',
      comments: distribution?.comment || '',
      silos: siloFormValues?.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' })) || [],
    };
  }, [silos, distribution]);

  const form = useForm<CreateOrderDistributionForm>({
    defaultValues,
    mode: 'onSubmit',
    reValidateMode: 'onBlur',
  });

  const watchedValues = useWatch({
    control: form.control,
  });

  const { fields } = useFieldArray({
    control: form.control,
    name: 'silos',
  });

  //#endregion FORMS

  //#region HANDLERS

  const handleCreateDistribution = (data: CreateOrderDistributionForm) => {
    const distributions = data.silos.flatMap(({ siloDistributions }) => siloDistributions.filter(({ itemNumber }) => !!itemNumber));

    postDistribution.mutate({ comment: data.comments, pointOfContact: data.pointOfContact, items: distributions });
  };

  //#endregion HANDLERS

  //#region HOOKS

  useEffect(() => {
    const distributions = (watchedValues.silos?.flatMap(
      ({ siloDistributions }) => siloDistributions?.filter((d) => !!d.itemNumber && !!d.quantity) || []
    ) || []) as SiloDistribution[];

    const distributedQuantities = distributions.reduce<DistributedQuantities>((quantities, { itemNumber, quantity }) => {
      quantities[itemNumber] = Number(quantities[itemNumber]) || 0;
      quantities[itemNumber] += Number(quantity);
      return quantities;
    }, {});

    setDistributed(distributedQuantities);
  }, [watchedValues]);

  useEffect(() => {
    form.reset(defaultValues);
  }, [defaultValues]);

  //#endregion HOOKS

  return isDistributionLoading ? (
    <LoadingSpinner />
  ) : distribution ? (
    <>
      <Alert severity="info">
        <FormattedMessage id={`${PAGE_NAME_ID}.infoText`} />
      </Alert>

      <Paper
        sx={{
          backgroundColor: theme.palette.lightBlue.o25,
          padding: 5,
        }}>
        <Box display={'flex'} flexDirection={'column'} gap={4}>
          <List options={options} dark>
            <ListRow
              key={`distribution_row_${0}`}
              body={{
                open: true,
                options: bodyOptions,
                span: 6,
                children: distribution?.products.map((product, idx) => {
                  const exceedsQuantity = distributed[product.itemNumber] > product.quantity;

                  const leftToDistribute = originalToDistribute[product.itemNumber] - distributed[product.itemNumber];

                  return (
                    <BodyTableRow key={`distribution_row_${0}_product_${idx}`}>
                      <ListCell sx={{ display: 'flex' }}>
                        <Tooltip title={exceedsQuantity ? intl.formatMessage({ id: `${PAGE_NAME_ID}.productAlert` }) : ''}>
                          <AlertTriangle
                            fill={theme.palette.warning.light}
                            color={theme.palette.darkBlue.main}
                            size={18}
                            visibility={exceedsQuantity ? 'visible' : 'hidden'}
                          />
                        </Tooltip>
                      </ListCell>
                      <ListCell>{product.itemName}</ListCell>
                      <ListCell align="right">
                        <FormattedNumber value={product.quantity} style={`unit`} unit="kilogram" />
                      </ListCell>
                      <ListCell align="right">
                        <span
                          style={{
                            color: leftToDistribute === 0 ? theme.palette.green.main : theme.palette.darkBlue.main,
                          }}>
                          <FormattedNumber
                            value={!!distributed[product.itemNumber] ? leftToDistribute : originalToDistribute[product.itemNumber]}
                            style={`unit`}
                            unit="kilogram"
                          />
                          {leftToDistribute === 0 && <span> (Completed)</span>}
                        </span>
                      </ListCell>
                      <ListCell align="right">
                        <FormattedNumber value={distributed[product.itemNumber] || 0} style={`unit`} unit="kilogram" />
                      </ListCell>
                    </BodyTableRow>
                  );
                }),
              }}>
              <ListCell>{distribution?.siteName}</ListCell>
              <ListCell>
                <FormattedDate value={distribution?.requestedDeliveryDate} />
              </ListCell>
              <ListCell>
                <FormattedDate value={distribution?.eta} />
                &nbsp;
                <FormattedTime value={distribution?.eta} />
              </ListCell>
              <ListCell align="right">
                <FormattedNumber
                  value={distribution?.products.reduce((total, product) => total + product.quantity, 0) || 0}
                  style={`unit`}
                  unit="kilogram"
                />
              </ListCell>
            </ListRow>
          </List>

          <form onSubmit={form.handleSubmit(handleCreateDistribution)}>
            {isSilosLoading ? (
              <LoadingSpinner />
            ) : (
              <Box display={'flex'} flexDirection={'column'} gap={2}>
                <Box
                  display={'flex'}
                  alignItems={'center'}
                  justifyContent={'space-between'}
                  flexDirection={isMobile ? 'column' : 'row'}>
                  <Typography>
                    <FormattedMessage id={`${PAGE_NAME_ID}.distribute`} />
                  </Typography>
                  <Box display={'flex'} flexDirection={isMobile ? 'column' : 'row'} gap={2} width={isMobile ? '100%' : 'auto'}>
                    <Button variant="outlined" disabled={postDistribution.isPending} onClick={() => form.reset(defaultValues)}>
                      <FormattedMessage id={`general.reset`} />
                    </Button>
                    <Button
                      type="submit"
                      startIcon={postDistribution.isPending ? <CircularProgress color="inherit" size={18} /> : null}
                      disabled={postDistribution.isPending}>
                      <FormattedMessage id={`general.save`} />
                    </Button>
                  </Box>
                </Box>
                {fields.map((field, idx) => {
                  const silo = {
                    name: field.name,
                    capacity: field.capacity,
                    id: field.siloId,
                    isExtraStorage: field.isExtraStorage,
                    hasConfigChanged: field.hasConfigChanged,
                    description: field.description,
                    isEnabled: field.isEnabled,
                  };

                  return (
                    <Box key={field.id}>
                      <List options={[]}>
                        <DistributeSiloForm
                          form={form}
                          distribution={distribution}
                          originalToDistribute={originalToDistribute}
                          distributed={distributed}
                          nestedIndex={idx}
                          silo={silo}
                        />
                      </List>
                      <Divider dark />
                    </Box>
                  );
                })}
                <Box display={'flex'} flexDirection={'column'} gap={2}>
                  <Typography>
                    <FormattedMessage id={`${PAGE_NAME_ID}.additionalNotes`} />
                  </Typography>
                  <Box display={'flex'} flexDirection={'column'} gap={4}>
                    <Box width={{ xs: '100%', md: '50%' }}>
                      {isSitesSettingsLoading ? (
                        <LoadingSpinner />
                      ) : (
                        <Select
                          label={intl.formatMessage({ id: `${PAGE_NAME_ID}.form.pointOfContact` })}
                          dark
                          noResults={{
                            show: !siteSettings?.phoneNumbers.length,
                            text: intl.formatMessage({ id: 'general.noResults' }),
                          }}
                          sx={{
                            backgroundColor: 'white',
                            borderRadius: 2,
                          }}
                          controller={{
                            control: form.control,
                            name: 'pointOfContact',
                          }}>
                          {siteSettings?.phoneNumbers?.map((phoneNumber, idx) => (
                            <RadioSelectItem key={idx} value={phoneNumber}>
                              {formatPhoneNumber(phoneNumber)}
                            </RadioSelectItem>
                          ))}
                        </Select>
                      )}
                    </Box>
                    <TextField
                      label={intl.formatMessage({ id: `${PAGE_NAME_ID}.form.comments` })}
                      dark
                      fullWidth
                      multiline
                      sx={{
                        backgroundColor: 'white',
                        borderRadius: 2,
                      }}
                      maxRows={4}
                      controller={{
                        control: form.control,
                        name: 'comments',
                      }}
                    />
                    <Box display={'flex'} flexDirection={'column'} gap={2}>
                      <Box display={'flex'} flexDirection={'row'} justifyContent={'space-between'}>
                        <Typography fontWeight={'bold'}>
                          <FormattedMessage id={`${PAGE_NAME_ID}.comments`} />
                        </Typography>
                        <Typography>
                          <FormattedMessage id={`${PAGE_NAME_ID}.editable`} />
                        </Typography>
                      </Box>

                      <TextField
                        label={intl.formatMessage({ id: `${PAGE_NAME_ID}.comments` })}
                        dark
                        fullWidth
                        multiline
                        disabled
                        hiddenLabel
                        maxRows={4}
                        value={distribution?.siteComment ?? ''}
                      />
                    </Box>
                  </Box>
                </Box>
              </Box>
            )}
          </form>
        </Box>
      </Paper>
    </>
  ) : null;
};

export default React.memo(CreateOrderDistribution);
